/**
 * @namespace Image
 * @memberof Promega.Components
 * @description Methods for controlling expandable images. By default, an image can be expanded on click if it has the <code>data-expandable-behavior</code> attribute set to <code>"modal"</code>.
 * When clicked, an image with this attribute will animate to a larger size and dim the rest of the page. If the image's <code>nextElementSibling</code> is a <code>&lt;figcaption&gt;</code>
 * element, the contents of that element will also be displayed in the fullscreen view when the image is expanded. The methods provided by this namespace allow you to manually trigger
 * an image to expand or collapse from JS code, independent of the default behavior.<br><br>
 * This component makes use of the [HTML5 History API]{@link https://developer.mozilla.org/en-US/docs/Web/API/History}. Expanding an image adds an entry to your browser history,
 * and collapsing the image pops the most recent entry from your browser history. Therefore, when an image is expanded, pressing the "back" button in your browser will cause
 * the image to collapse back to its normal size, and then pressing the "forward" button in your browser will trigger the expansion again. This is particularly useful on
 * mobile devices that include a "back" button as part of their default hardware or software UI; for example, if you expand an image on a Samsung Galaxy phone you can
 * collapse it again by pressing your device's system "back" button.
 * 
 * @example <caption>An image with <code>data-expandable-behavior="modal"</code> will be automatically expanded when clicked.</caption>
 * <img data-expandable-behavior="modal" src="path-to-the-image.jpg" alt="An image that expands on click" />
 * 
 * @example <caption>If a <code>&lt;figcaption&gt;</code> element immediately follows the image, its contents will be used as a caption when the image is in its expanded state.</caption>
 * <img data-expandable-behavior="modal" src="path-to-the-image.jpg" alt="An image that expands on click" />
 * <figcaption>This is a caption to go along with the image.</figcaption>
 */

import throttle from '../../util/throttle';
import isAnimationSupported from '../../util/isAnimationSupported';
import trackEvent from '../../util/trackEvent';

const EXPANDED_CLASS = "content-item__image--expanded",
    EXPANDED_CLASS_CAPTION = "content-item__caption--expanded",
    TRANSITIONING_CLASS = "content-item__image--transitioning",
    CLICKEATER_CLASS = "content-item__image-clickeater",
    CLICKEATER_HIDING_CLASS = "content-item__image-clickeater--hiding";

let currentSpaceholder = null;
let collapsedManually = false;

// Private functions/listeners
function imageExpandOnClickListener (event) {
    if (event.target && event.target.nodeName.toLowerCase() === "img") {
        const expandableBehavior = event.target.getAttribute("data-expandable-behavior");
        if (expandableBehavior === "modal") {
            if (!event.target.classList.contains(EXPANDED_CLASS)) {
                trackEvent("Components", "Expandable image - expanded an image", "Trigger element");
                expandImage(event.target);
            }
            else {
                collapsedManually = true;
                collapseImage();
                trackEvent("Components", "Expandable image - collapsed an image", "Trigger element");
            }
        }
    }
}

function expandableImageTransitioned (event) {
    event.currentTarget.removeEventListener("transitionend", expandableImageTransitioned);
    event.currentTarget.classList.remove(TRANSITIONING_CLASS);
    event.currentTarget.style.transform = "";
}

function documentKeydown (event) {
    if (event.keyCode === 27) {
        const transitioningImage = document.getElementsByClassName(TRANSITIONING_CLASS)[0];
        if (!transitioningImage) {
            event.preventDefault();
            collapsedManually = true;
            collapseImage();
            trackEvent("Components", "Expandable image - collapsed an image", "Esc key");
            document.removeEventListener("keydown", documentKeydown);
        }
    }
}

function createClickeater () {
    const imgClickeater = document.createElement("div");
    imgClickeater.className = CLICKEATER_CLASS;
    imgClickeater.addEventListener("click", () => {
        collapsedManually = true;
        collapseImage();
        trackEvent("Components", "Expandable image - collapsed an image", "Backdrop click");
    });
    document.body.appendChild(imgClickeater);
    document.addEventListener("keydown", documentKeydown);
}

function hideClickeater () {
    const imgClickeater = document.getElementsByClassName(CLICKEATER_CLASS)[0];
    if (imgClickeater) {
        if (isAnimationSupported()) {
            imgClickeater.addEventListener("animationend", removeClickeater);
            imgClickeater.classList.add(CLICKEATER_HIDING_CLASS);
        }
        else removeClickeater();
    }
}

function removeClickeater () {
    const imgClickeater = document.getElementsByClassName(CLICKEATER_CLASS)[0];
    if (imgClickeater) {
        imgClickeater.parentNode.removeChild(imgClickeater);
    }
}

function windowResizeOrScroll(event) {
    if (isImageExpanded()) {
        collapsedManually = true;
        collapseImage();
        trackEvent("Components", "Expandable image - collapsed an image", "Scrolling");
    }
}

function popstateHandler (event) {
    if (event.state && event.state.type === "image" && event.state.data && event.state.data.src) {
        // Find the image with the saved src and expand it
        const imageToExpand = document.querySelector(`img[src='${event.state.data.src}'`);
        if (imageToExpand) {
            expandImage(imageToExpand, { omitFromHistory: true });
            if (!collapsedManually)
                trackEvent("Components", "Expandable image - expanded an image", "Browser history");
            else collapsedManually = false;
        }
    }
    else if (isImageExpanded()) {
        _collapseImage();
        if (!collapsedManually)
            trackEvent("Components", "Expandable image - collapsed an image", "Browser history");
        else collapsedManually = false;
    }
}

// API

/**
 * @function expandImage
 * @memberof Promega.Components.Image
 * @description Animates the given HTML image element to a larger size and dims the page beneath it.
 * @param {HTMLImageElement} imageEl The image element to expand
 */
export function expandImage (imageEl, options) {
    // Expands the given image element.
    if (imageEl && !isImageExpanded() && !imageEl.classList.contains(TRANSITIONING_CLASS)) {
        // Get the width and height of the image to create a spaceholder
        const imageStyle = getComputedStyle(imageEl),
            imageWidth = imageEl.offsetWidth,
            imageHeight = imageEl.offsetHeight,
            marginBottom = parseInt(imageStyle.marginBottom, 10),
            proportion = (imageHeight + marginBottom) / imageWidth,
            percentage = 100 * proportion + "%";

        const outerEl = document.createElement("div"),
            innerEl = document.createElement("div");
        outerEl.style.width = imageWidth + "px";
        outerEl.style.maxWidth = "100%";
        innerEl.style.width = "100%";
        innerEl.style.paddingTop = percentage;

        //Adds the caption if one exists
        if(imageEl.nextElementSibling && imageEl.nextElementSibling.tagName.toLowerCase() === "figcaption"){
            const captionText = imageEl.nextElementSibling.innerHTML,
                captionEl = document.createElement("div"),
                captionInnerEl = document.createElement("div");

            const toggleArrowEl = document.createElement("div");
            toggleArrowEl.className = "content-item__caption-button content-item__caption-button--expanded";
            toggleArrowEl.addEventListener("click", function(event){
                if (event.currentTarget.parentNode.classList.contains("content-item__caption--hide")){
                    event.currentTarget.parentNode.classList.remove("content-item__caption--hide");
                }else{
                    event.currentTarget.parentNode.classList.add("content-item__caption--hide");
                }
            })

            captionInnerEl.innerHTML = captionText;
            captionEl.classList.add(EXPANDED_CLASS_CAPTION);
            captionInnerEl.classList.add('content-width');
            captionEl.appendChild(toggleArrowEl);
            captionEl.appendChild(captionInnerEl);
            outerEl.appendChild(captionEl);
        }

        // Calculate the X,Y offset if the image were centered on the screen at the current size
        const boundingClientRect = imageEl.getBoundingClientRect(),
            imageTop = boundingClientRect.top,
            imageLeft = boundingClientRect.left,
            scrollbarWidth = window.innerWidth - document.body.clientWidth,
            windowWidth = window.innerWidth - scrollbarWidth,
            screenWidthCenter = windowWidth / 2,
            screenHeightCenter = window.innerHeight / 2,
            imageLeftIfCentered = screenWidthCenter - (0.5 * imageWidth),
            imageTopIfCentered = screenHeightCenter - (0.5 * imageHeight);                

        // Calculate the difference in scale at the size it will be when expanded (1280px or the width of the screen, whichever is smaller)

        const maxExpandedWidth = 1280;
                
        let finalExpandedWidth = maxExpandedWidth;
        //sets finalExpandedWidth depending on orientation of image 
        if (proportion > 1) finalExpandedWidth = window.innerHeight / proportion;
        else if (windowWidth < finalExpandedWidth) finalExpandedWidth = windowWidth;

        const oldScale = imageWidth / finalExpandedWidth;
                
        
        // Build the transform string
        let transformString = "translate3d(" + (imageLeft - imageLeftIfCentered) + "px," + (imageTop - imageTopIfCentered) + "px, 0) ";
        transformString += "scale(" + oldScale + ")";
                
        outerEl.appendChild(innerEl);
                
        imageEl.parentNode.insertBefore(outerEl, imageEl.nextElementSibling);
        currentSpaceholder = outerEl;

        imageEl.classList.add(EXPANDED_CLASS);
        //if image height > width, the image fits the screen based on its height
        if(proportion > 1){
            imageEl.style.width = finalExpandedWidth + 'px';
        }

        if (isAnimationSupported()) {
            imageEl.style.transform = transformString;
            imageEl.offsetHeight; // Force the browser to calculate reflow before applying the transition
            imageEl.addEventListener("transitionend", expandableImageTransitioned);
            imageEl.classList.add(TRANSITIONING_CLASS);
            imageEl.style.transform = "";
        }

        createClickeater();

        if (!(options && options.omitFromHistory)) history.pushState({ type: "image", data: { src: imageEl.getAttribute("src") }}, "", "");
    }
}

/**
 * @function collapseImage
 * @memberof Promega.Components.Image
 * @description Collapses the currently expanded image back to its original size. Has no effect if no images are expanded.
 */
export function collapseImage() {
    if (isImageExpanded() && !document.querySelector(`.${TRANSITIONING_CLASS}`)) history.back();
}
function _collapseImage() {
    // Collapses the currently expanded image, if any.
    const imageToCollapse = document.getElementsByClassName(EXPANDED_CLASS)[0];
    if (imageToCollapse && currentSpaceholder && !imageToCollapse.classList.contains(TRANSITIONING_CLASS)) {
        // Calculate the X,Y offset if the image were centered on the screen at the current size
        const boundingClientRect = imageToCollapse.getBoundingClientRect(),
            spaceholderClientRect = currentSpaceholder.getBoundingClientRect(),
            spaceholderTop = spaceholderClientRect.top,
            spaceholderLeft = spaceholderClientRect.left,
            scrollbarWidth = window.innerWidth - document.body.clientWidth,
            windowWidth = window.innerWidth - scrollbarWidth,
            imageLeftIfCentered = (windowWidth / 2) - (0.5 * spaceholderClientRect.width),
            imageTopIfCentered = (window.innerHeight / 2) - (0.5 * spaceholderClientRect.height);

        // Calculate the difference in scale at the size it will be when expanded (1280px or the width of the screen, whichever is smaller)
        const oldScale = boundingClientRect.width / spaceholderClientRect.width;

        // Build the transform string
        let transformString = "translate3d(" + (imageLeftIfCentered - spaceholderLeft) + "px," + (imageTopIfCentered - spaceholderTop) + "px, 0) ";
        transformString += "scale(" + oldScale + ")";

        imageToCollapse.classList.remove(EXPANDED_CLASS);
        //if image height > width, style needs to be removed
        imageToCollapse.style.width = null;

        if (isAnimationSupported()) {
            imageToCollapse.style.transform = transformString;
            imageToCollapse.offsetHeight; // Force the browser to calculate reflow before applying the transition
            imageToCollapse.addEventListener("transitionend", expandableImageTransitioned);
            imageToCollapse.classList.add(TRANSITIONING_CLASS);
            imageToCollapse.style.transform = "";
        }
                
        currentSpaceholder.parentNode.removeChild(currentSpaceholder);
        currentSpaceholder = null;

        hideClickeater();

        if (imageToCollapse.nodeName.toLowerCase() === "video" && imageToCollapse.getAttribute("data-playback-behavior") === "modal") {
            imageToCollapse.removeAttribute("controls");
            imageToCollapse.pause();
        }
    }
}

/**
 * @function isImageExpanded
 * @memberof Promega.Components.Image
 * @description Returns a value indicating whether or not an image is curently expanded on the page.
 * @returns {Boolean}
 */
export function isImageExpanded () {
    // Returns true if an image is expanded on the current page, or false if no images are expanded.
    let returnVal = false;
    if (document.getElementsByClassName(EXPANDED_CLASS)[0]) returnVal = true;
    return returnVal; 
}
    

// Init code

{
    document.addEventListener("click", imageExpandOnClickListener);
    document.addEventListener("scroll", throttle(windowResizeOrScroll, 100));
    window.addEventListener("resize", throttle(windowResizeOrScroll, 100));
    window.addEventListener("popstate", popstateHandler);
}
